import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Typography,
    TextField,
    Paper,
    useTheme,
    ToggleButton,
    ToggleButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    Checkbox,
    FormGroup,
    CircularProgress,
    LinearProgress,
    IconButton,
    Tooltip,
    Alert as MuiAlert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSignOut } from 'react-auth-kit';
import Axios from 'axios';
import Header from '../../components/Header';
import { tokens } from '../../theme';
import { getAuthHeader } from '../../utils/authToken';
import { pollRegenJob, RegenJobError } from '../../utils/pollRegenJob';
import Alert from '../../components/alert/Alert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/Publish';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

/**
 * Editor body for admission-type newsletters.
 *
 * Data shape (from server):
 *   newsletter.content_data = {
 *     intro, outro, layout, style, hero_image_url, candidates_count,
 *     all_candidates,
 *     top3: [{ korean_headline, korean_description, korean_summary,
 *              original_title, url, image_url }],
 *   }
 *
 * Editing surface (per spec): admin can edit intro_text, outro_text, and
 * each top-3 story's headline/description/summary/url. Image swapping
 * is out of v1 scope (will come once boss confirms layout/style).
 */
const AdmissionNewsletterEditor = ({ newsletter, onReload }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();
    const signOut = useSignOut();

    const [viewMode, setViewMode] = useState('preview'); // 'preview' | 'edit'

    const cd = newsletter.content_data || {};
    const initialTop3 = Array.isArray(cd.top3) ? cd.top3 : [];

    const [titleText, setTitleText] = useState(newsletter.title || '');
    const [introText, setIntroText] = useState(newsletter.intro_text || cd.intro || '');
    const [outroText, setOutroText] = useState(newsletter.outro_text || cd.outro || '');
    const [issueLabel, setIssueLabel] = useState(cd.issue_label !== undefined ? cd.issue_label : 'Issue 01');
    const [readTime, setReadTime] = useState(cd.read_time !== undefined ? cd.read_time : '');
    const [stories, setStories] = useState(initialTop3.map((s) => ({ ...s })));

    const [saving, setSaving] = useState(false);
    const [updatingHtml, setUpdatingHtml] = useState(false);
    // Only one regen runs at a time. Locking serially keeps the state simple
    // and matches admin reality (you don't usually want to fire two in
    // parallel — it's expensive AI work and easy to lose track of).
    // Shape: { kind: 'story' | 'image', index, startedAt } | null
    const [currentRegen, setCurrentRegen] = useState(null);
    const [regenStoryConfirm, setRegenStoryConfirm] = useState({ open: false, index: -1 });
    const [removeStoryConfirm, setRemoveStoryConfirm] = useState({ open: false, index: -1 });
    // After a regen completes, hold the OLD vs NEW for admin's swap-or-keep
    // decision. Shape: { open, index, oldStory, newStory } | null
    // The new story is already committed to the DB by the time this opens —
    // "Keep new" is a no-op (just close). "Revert" issues a PUT that swaps
    // top3 back and updates excluded_stories accordingly.
    const [swapDecision, setSwapDecision] = useState(null);
    const [reverting, setReverting] = useState(false);
    // Image-only preview decision. Shape: { open, index, oldImageUrl, newImageUrl } | null
    // Set by handleRegenerateImage once the preview returns. The new image
    // is NOT committed to the DB until "Keep" — discard simply closes the modal
    // (the preview blob lingers in S3, orphaned but harmless).
    const [imageDecision, setImageDecision] = useState(null);
    const [committingImage, setCommittingImage] = useState(false);
    // Click-to-enlarge for story-slot thumbnails. Holds the URL of the image
    // to display at full size, or null when the dialog is closed.
    const [enlargedImageUrl, setEnlargedImageUrl] = useState(null);
    // Per-regen style-cues tweak dialog. Opens when admin clicks "Regenerate
    // image" on a slot — prefilled with default cues, admin can tweak (or just
    // Generate). One-off: cues don't persist. Shape: { open, index, cues } | null
    const [styleDialog, setStyleDialog] = useState(null);
    // Working draft of the exclude list for whichever slot is being regenerated.
    // Each item is { url, title }. Initialized when the dialog opens with the
    // newsletter's persisted excluded_stories + the URL of the slot being
    // replaced (so admin can see/remove it; backend auto-adds it on success).
    const [dialogExcludeList, setDialogExcludeList] = useState([]);
    const [dialogAddUrl, setDialogAddUrl] = useState('');
    const [dialogAddTitle, setDialogAddTitle] = useState('');
    const [dialogAddError, setDialogAddError] = useState('');
    const [tickTock, setTickTock] = useState(0);              // bump to redraw progress bars

    // Tick once per second while a regen is in flight to advance the bar.
    useEffect(() => {
        if (!currentRegen) return undefined;
        const id = setInterval(() => setTickTock((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, [currentRegen]);

    // Abort any in-flight regen polling loop on unmount.
    const regenAbortRef = useRef(null);
    useEffect(() => () => regenAbortRef.current?.abort(), []);
    // Full-newsletter regenerate confirmation (destructive — replaces row content).
    const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    // Preview viewport: 'desktop' (full width) | 'mobile' (~375px constrained).
    const [previewViewport, setPreviewViewport] = useState('desktop');

    // Publish dialog
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [sendViaMailerLite, setSendViaMailerLite] = useState(false);
    const [mailerliteGroups, setMailerliteGroups] = useState([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [groupsError, setGroupsError] = useState(null);
    const [publishing, setPublishing] = useState(false);
    const [unpublishing, setUnpublishing] = useState(false);

    // Alerts
    const [successAlertOpen, setSuccessAlertOpen] = useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = useState('');
    const [errorAlertOpen, setErrorAlertOpen] = useState(false);
    const [errorAlertMessage, setErrorAlertMessage] = useState('');

    const handleLogout = () => {
        signOut();
        localStorage.removeItem('userName');
        localStorage.removeItem('who');
        navigate('/signin');
    };

    // Re-sync local state when newsletter reloads from parent.
    useEffect(() => {
        const cdNow = newsletter.content_data || {};
        setTitleText(newsletter.title || '');
        setIntroText(newsletter.intro_text || cdNow.intro || '');
        setOutroText(newsletter.outro_text || cdNow.outro || '');
        setIssueLabel(cdNow.issue_label !== undefined ? cdNow.issue_label : 'Issue 01');
        setReadTime(cdNow.read_time !== undefined ? cdNow.read_time : '');
        setStories(Array.isArray(cdNow.top3) ? cdNow.top3.map((s) => ({ ...s })) : []);
    }, [newsletter]);

    const updateStory = (index, field, value) => {
        setStories((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
    };

    /**
     * Append an empty story slot. Capped at 3.
     * The slot is fully empty — admin can fill in fields manually or click
     * "Regenerate story" to fetch fresh content from a web search.
     */
    const addStory = () => {
        setStories((prev) => {
            if (prev.length >= 3) return prev;
            return [...prev, {
                korean_headline: '',
                korean_description: '',
                korean_summary: '',
                original_title: '',
                url: '',
                image_url: null,
            }];
        });
    };

    /**
     * Swap-or-keep revert. The NEW story is already committed to the DB
     * (handleRegenerateStory writes it under the row lock). Reverting means:
     *
     *   1. Put the OLD story back into top3[index] (local state + PUT).
     *   2. Update excluded_stories: REMOVE old.url (admin clearly likes it
     *      after all — the regen helper auto-added it on success), and ADD
     *      new.url (admin rejected it; needs persisted exclusion so future
     *      regens don't surface it again).
     *
     * No new endpoints — uses the existing PUT contract with the now-extended
     * excluded_stories field.
     */
    const revertSwapDecision = async () => {
        if (!swapDecision) return;
        const { index, oldStory, newStory } = swapDecision;
        setReverting(true);
        try {
            const updatedStories = stories.map((s, i) => (i === index ? oldStory : s));
            const persistedExcluded = Array.isArray(newsletter.content_data?.excluded_stories)
                ? newsletter.content_data.excluded_stories
                : [];
            const oldKey = (oldStory.url || '').toLowerCase();
            const newEntry = newStory.url
                ? { url: newStory.url, title: newStory.original_title || newStory.korean_headline || '' }
                : null;
            const updatedExcluded = persistedExcluded
                .filter((e) => (e?.url || '').toLowerCase() !== oldKey);
            if (newEntry) updatedExcluded.push(newEntry);

            await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletter.uuid}`,
                {
                    title: titleText.trim(),
                    intro_text: introText,
                    outro_text: outroText,
                    issue_label: issueLabel,
                    read_time: readTime,
                    top3: updatedStories,
                    excluded_stories: updatedExcluded,
                },
                { headers: { Authorization: getAuthHeader() } }
            );
            setStories(updatedStories);
            setSwapDecision(null);
            setSuccessAlertMessage(`Reverted Story ${index + 1} to the previous version`);
            setSuccessAlertOpen(true);
            onReload?.();
        } catch (error) {
            if (error.response?.status === 401) handleLogout();
            else {
                setErrorAlertMessage(error.response?.data?.error || 'Failed to revert');
                setErrorAlertOpen(true);
            }
        } finally {
            setReverting(false);
        }
    };

    /** Remove the story at the given index after confirmation. */
    const executeRemoveStory = () => {
        const idx = removeStoryConfirm.index;
        if (idx < 0) return;
        setStories((prev) => prev.filter((_, i) => i !== idx));
        setRemoveStoryConfirm({ open: false, index: -1 });
    };

    /**
     * Render a mini progress strip for one slot — only if THIS slot is the
     * currently-running regen. Single honest label + elapsed-seconds counter +
     * indeterminate bar. Model-agnostic; no fake stage transitions to retune
     * when models change.
     */
    function regenProgress(kind /* 'story' | 'image' */, index) {
        if (!currentRegen || currentRegen.kind !== kind || currentRegen.index !== index) return null;
        const elapsedSec = Math.floor((Date.now() - currentRegen.startedAt) / 1000);
        const label = kind === 'story' ? 'Regenerating story…' : 'Regenerating image…';
        return (
            <Box
                sx={{
                    mt: 2,
                    mb: kind === 'story' ? 2.5 : 1.5,
                    px: 1.5,
                    py: 1.25,
                    backgroundColor: colors.primary[700],
                    borderLeft: '3px solid',
                    borderColor: 'warning.main',
                    borderRadius: 1,
                }}
            >
                <Box display="flex" justifyContent="space-between" mb={0.75}>
                    <Typography variant="caption" sx={{ color: colors.grey[200] }}>{label}</Typography>
                    <Typography variant="caption" sx={{ color: colors.grey[400], fontVariantNumeric: 'tabular-nums' }}>{elapsedSec}s</Typography>
                </Box>
                <LinearProgress color="warning" sx={{ height: 5, borderRadius: 2 }} />
            </Box>
        );
    }

    // Live "X-minute read" suggestion computed from the current edit state.
    // Mirrors helpers/admission_newsletter_generation_helper.js#computeReadTimeLabel
    // (Korean ~400 chars/min, rounded up, min 1).
    const suggestedReadTime = (() => {
        const parts = [
            introText || '',
            outroText || '',
            ...stories.flatMap((s) => [s?.korean_headline || '', s?.korean_description || '', s?.korean_summary || '']),
        ];
        const totalChars = parts.join('').length;
        const minutes = Math.max(1, Math.ceil(totalChars / 400));
        return `${minutes}-minute read`;
    })();
    const suggestedIssueLabel = 'Issue 01';

    // On Tab in an empty field, prefill with the suggestion. Do NOT preventDefault
    // so the focus still moves to the next field after the prefill — single-Tab
    // gives "autofill + advance".
    function handleSuggestionTab(currentValue, suggestion, setter) {
        return (e) => {
            if (e.key === 'Tab' && (!currentValue || !currentValue.trim()) && suggestion) {
                setter(suggestion);
            }
        };
    }

    /**
     * Send the current edit state to the server. Does NOT show any UI alerts —
     * caller decides whether to surface success/error.
     * Throws on failure so callers can chain (e.g. save-then-regen).
     */
    const persistDraft = async () => {
        if (!titleText || !titleText.trim()) {
            const err = new Error('Title cannot be empty');
            err.userMessage = 'Title cannot be empty';
            throw err;
        }
        await Axios.put(
            `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletter.uuid}`,
            {
                title: titleText.trim(),
                intro_text: introText,
                outro_text: outroText,
                issue_label: issueLabel,
                read_time: readTime,
                top3: stories,
            },
            { headers: { Authorization: getAuthHeader() } }
        );
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await persistDraft();
            setSuccessAlertMessage('Admission newsletter saved');
            setSuccessAlertOpen(true);
            onReload?.();
        } catch (error) {
            if (error.response?.status === 401) handleLogout();
            else {
                setErrorAlertMessage(error.userMessage || error.response?.data?.error || 'Failed to save newsletter');
                setErrorAlertOpen(true);
            }
        } finally {
            setSaving(false);
        }
    };

    /**
     * Open the confirm dialog. The dialog shows two groups:
     *   - "Currently in this newsletter" — read-only, all 3 top3 stories.
     *     The backend auto-excludes these regardless, so they're transparent.
     *   - "Other excluded stories" — editable rejection history (the working
     *     dialogExcludeList state). Seeded from persisted excluded_stories,
     *     filtered to drop anything that's already in top3 (would be redundant).
     */
    const openRegenStoryDialog = (index) => {
        const persisted = Array.isArray(newsletter.content_data?.excluded_stories)
            ? newsletter.content_data.excluded_stories
            : [];
        const currentUrls = new Set(
            stories.map((s) => s?.url?.toLowerCase()).filter(Boolean),
        );
        const seen = new Set();
        const history = persisted.filter((e) => {
            if (!e?.url) return false;
            const k = e.url.toLowerCase();
            if (currentUrls.has(k)) return false;
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        });
        setDialogExcludeList(history);
        setDialogAddUrl('');
        setDialogAddTitle('');
        setDialogAddError('');
        setRegenStoryConfirm({ open: true, index });
    };

    const removeFromExcludeList = (url) => {
        const k = url.toLowerCase();
        setDialogExcludeList((prev) => prev.filter((e) => e.url.toLowerCase() !== k));
    };

    const addToExcludeList = () => {
        const url = dialogAddUrl.trim();
        if (!url) {
            setDialogAddError('Enter a URL');
            return;
        }
        try {
            // eslint-disable-next-line no-new
            new URL(url);
        } catch (_e) {
            setDialogAddError('Not a valid URL');
            return;
        }
        const k = url.toLowerCase();
        if (dialogExcludeList.some((e) => e.url.toLowerCase() === k)) {
            setDialogAddError('Already in the list');
            return;
        }
        if (stories.some((s) => s?.url?.toLowerCase() === k)) {
            setDialogAddError('That story is already in this newsletter (auto-excluded)');
            return;
        }
        setDialogExcludeList((prev) => [...prev, { url, title: dialogAddTitle.trim() }]);
        setDialogAddUrl('');
        setDialogAddTitle('');
        setDialogAddError('');
    };

    /**
     * Per-story regenerate. Closes the confirm dialog immediately and shows
     * a mini progress bar inside the card. Multiple regens can run in
     * parallel — backend serves them as separate HTTP requests.
     */
    const handleRegenerateStory = async (index) => {
        if (currentRegen) return; // belt-and-suspenders; UI already disables
        // Capture the exclude list at submit time so the value is stable
        // even if the dialog re-mounts.
        const excluded_stories = dialogExcludeList.map((e) => ({ url: e.url, title: e.title || '' }));
        // Snapshot the OLD story BEFORE regen so swap-or-keep can offer revert.
        // Deep-copy via JSON round-trip — story is plain data, no functions.
        const oldStorySnapshot = JSON.parse(JSON.stringify(stories[index] || {}));
        setRegenStoryConfirm({ open: false, index: -1 });
        setCurrentRegen({ kind: 'story', index, startedAt: Date.now() });
        try {
            // Auto-save current draft state first so the backend sees any
            // newly-added empty slots (and any other unsaved local edits).
            // Without this, regen on a slot the admin just added via "Add Story"
            // would 400 with "Invalid index N: top3 has M stories" because the
            // DB row doesn't yet have that slot.
            await persistDraft();

            // Enqueue the background job, then poll until it finishes. The
            // server returns 202 { job_id } immediately so the long web-search +
            // AI rewrite can't trip an upstream request timeout.
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletter.uuid}/regenerate-story`,
                { index, excluded_stories },
                { headers: { Authorization: getAuthHeader() } }
            );
            const controller = new AbortController();
            regenAbortRef.current = controller;
            const result = await pollRegenJob(response.data.job_id, { signal: controller.signal });
            const ns = result.story;
            setStories((prev) => prev.map((s, i) => (i === index ? ns : s)));
            // Open the swap-or-keep modal with both stories side-by-side.
            // The new story is already committed to the DB — admin can either
            // accept it (no-op) or revert (issues a follow-up PUT). The
            // success alert is suppressed because the modal IS the success
            // signal; throwing a snackbar on top of it is noisy.
            setSwapDecision({
                open: true,
                index,
                oldStory: oldStorySnapshot,
                newStory: ns,
            });
            onReload?.();
        } catch (error) {
            if (error instanceof RegenJobError && error.code === 'ABORTED') {
                // Component unmounted / navigated away — nothing to surface.
            } else if (error.response?.status === 401) handleLogout();
            else if ((error instanceof RegenJobError && error.code === 'NO_FRESH_STORIES')
                || error.response?.data?.code === 'NO_FRESH_STORIES') {
                setErrorAlertMessage(error.message || error.response?.data?.error || 'No new stories available right now — try again in a moment.');
                setErrorAlertOpen(true);
            } else {
                setErrorAlertMessage(error.userMessage || error.response?.data?.error || error.message || 'Failed to regenerate story');
                setErrorAlertOpen(true);
            }
        } finally {
            regenAbortRef.current = null;
            setCurrentRegen(null);
        }
    };

    /**
     * Default style cues for the per-story image prompt-crafting step.
     * Mirrored verbatim on the server (getDefaultStoryStyleCues in
     * admission_newsletter_generation_helper.js). If you edit one, edit both.
     */
    const getDefaultStyleCues = (month) => {
        const seasons = {
            12: 'winter', 1: 'winter', 2: 'winter',
            3: 'spring', 4: 'spring', 5: 'spring',
            6: 'summer', 7: 'summer', 8: 'summer',
            9: 'fall', 10: 'fall', 11: 'fall',
        };
        const season = seasons[month] || 'seasonal';
        return `modern, soft professional palette, ${season} seasonal lighting, conceptually relates to the story without being literal`;
    };

    /** Open the style-tweak dialog for a slot. */
    const openStyleDialog = (index) => {
        if (currentRegen || imageDecision) return;
        setStyleDialog({
            open: true,
            index,
            cues: getDefaultStyleCues(newsletter.month),
        });
    };

    /**
     * Regenerate ONLY the image for one slot. Story content stays.
     * Serialized: only one regen of any kind runs at a time.
     *
     * Uses preview mode — the new image is uploaded to S3 but NOT persisted
     * to the DB until the admin clicks "Keep" in the resulting decision modal.
     *
     * @param {number} index - slot to regenerate
     * @param {string|null} styleOverride - optional one-off style cues
     */
    const handleRegenerateImage = async (index, styleOverride = null) => {
        if (currentRegen || imageDecision) return;
        setCurrentRegen({ kind: 'image', index, startedAt: Date.now() });
        const oldImageUrl = stories[index]?.image_url || null;
        try {
            // Auto-save first so any local headline/description edits are
            // reflected in the image prompt the backend crafts.
            await persistDraft();

            // Enqueue the background job, then poll until the image is ready.
            // 202 { job_id } returns immediately so gpt-image-2 generation can't
            // trip an upstream request timeout.
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletter.uuid}/regenerate-story-image`,
                { index, preview: true, style_override: styleOverride || undefined },
                { headers: { Authorization: getAuthHeader() } }
            );
            const controller = new AbortController();
            regenAbortRef.current = controller;
            const result = await pollRegenJob(response.data.job_id, { signal: controller.signal });
            const newImageUrl = result.image_url;
            setImageDecision({ open: true, index, oldImageUrl, newImageUrl });
        } catch (error) {
            if (error instanceof RegenJobError && error.code === 'ABORTED') {
                // Component unmounted / navigated away — nothing to surface.
            } else if (error.response?.status === 401) handleLogout();
            else {
                setErrorAlertMessage(error.userMessage || error.response?.data?.error || error.message || 'Failed to regenerate image');
                setErrorAlertOpen(true);
            }
        } finally {
            regenAbortRef.current = null;
            setCurrentRegen(null);
        }
    };

    /** Persist the previewed image to the DB. */
    const keepNewImage = async () => {
        if (!imageDecision) return;
        const { index, newImageUrl } = imageDecision;
        setCommittingImage(true);
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletter.uuid}/commit-story-image`,
                { index, image_url: newImageUrl },
                { headers: { Authorization: getAuthHeader() } }
            );
            setStories((prev) => prev.map((s, i) => (i === index ? { ...s, image_url: newImageUrl } : s)));
            setImageDecision(null);
            setSuccessAlertMessage(`Story ${index + 1} image saved`);
            setSuccessAlertOpen(true);
            onReload?.();
        } catch (error) {
            if (error.response?.status === 401) handleLogout();
            else {
                setErrorAlertMessage(error.response?.data?.error || 'Failed to save image');
                setErrorAlertOpen(true);
            }
        } finally {
            setCommittingImage(false);
        }
    };

    /** Throw the previewed image away. Old image stays. */
    const discardNewImage = () => {
        if (committingImage) return;
        setImageDecision(null);
    };

    const handleRegenerateHtml = async () => {
        setUpdatingHtml(true);
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletter.uuid}/regenerate-html`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            setSuccessAlertMessage('HTML regenerated');
            setSuccessAlertOpen(true);
            onReload?.();
        } catch (error) {
            if (error.response?.status === 401) handleLogout();
            else {
                setErrorAlertMessage(error.response?.data?.error || 'Failed to regenerate HTML');
                setErrorAlertOpen(true);
            }
        } finally {
            setUpdatingHtml(false);
        }
    };

    const handleDownloadHtml = async () => {
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletter.uuid}/html`,
                { headers: { Authorization: getAuthHeader() }, responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/html' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${newsletter.title}.html`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            if (error.response?.status === 401) handleLogout();
            else {
                setErrorAlertMessage('Failed to download HTML');
                setErrorAlertOpen(true);
            }
        }
    };

    const handleRetry = async () => {
        setRegenerating(true);
        try {
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletter.uuid}/retry`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            setSuccessAlertMessage('Regeneration started — current content will be replaced');
            setSuccessAlertOpen(true);
            setRegenerateConfirmOpen(false);
            navigate(`/admin/newsletters/progress/${response.data.uuid}`);
        } catch (error) {
            if (error.response?.status === 401) handleLogout();
            else {
                setErrorAlertMessage(error.response?.data?.error || 'Failed to start regeneration');
                setErrorAlertOpen(true);
            }
        } finally {
            setRegenerating(false);
        }
    };

    const handleDelete = async () => {
        try {
            await Axios.delete(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletter.uuid}`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setSuccessAlertMessage('Admission newsletter deleted');
            setSuccessAlertOpen(true);
            setDeleteDialogOpen(false);
            navigate('/admin/newsletters/admission');
        } catch (error) {
            if (error.response?.status === 401) handleLogout();
            else {
                setErrorAlertMessage('Failed to delete newsletter');
                setErrorAlertOpen(true);
            }
        }
    };

    const fetchMailerLiteGroups = async () => {
        setLoadingGroups(true);
        setGroupsError(null);
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/mailerlite/groups`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setMailerliteGroups(response.data.groups || []);
        } catch (error) {
            if (error.response?.status === 401) handleLogout();
            else setGroupsError(error.response?.data?.error || 'Failed to load groups');
        } finally {
            setLoadingGroups(false);
        }
    };

    const handleOpenPublish = () => {
        setSendViaMailerLite(false);
        setSelectedGroupIds([]);
        fetchMailerLiteGroups();
        setPublishDialogOpen(true);
    };

    const handlePublish = async () => {
        if (sendViaMailerLite && selectedGroupIds.length === 0) {
            setErrorAlertMessage('Please select at least one MailerLite group');
            setErrorAlertOpen(true);
            return;
        }
        setPublishing(true);
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletter.uuid}/publish`,
                { sendViaMailerLite, groupIds: selectedGroupIds },
                { headers: { Authorization: getAuthHeader() } }
            );
            setSuccessAlertMessage(sendViaMailerLite
                ? 'Newsletter published and sent via MailerLite'
                : 'Newsletter published');
            setSuccessAlertOpen(true);
            setPublishDialogOpen(false);
            onReload?.();
        } catch (error) {
            if (error.response?.status === 401) handleLogout();
            else {
                setErrorAlertMessage(error.response?.data?.error || 'Failed to publish');
                setErrorAlertOpen(true);
            }
        } finally {
            setPublishing(false);
        }
    };

    const handleUnpublish = async () => {
        setUnpublishing(true);
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletter.uuid}/unpublish`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            setSuccessAlertMessage('Newsletter unpublished');
            setSuccessAlertOpen(true);
            setUnpublishDialogOpen(false);
            onReload?.();
        } catch (error) {
            if (error.response?.status === 401) handleLogout();
            else {
                setErrorAlertMessage(error.response?.data?.error || 'Failed to unpublish');
                setErrorAlertOpen(true);
            }
        } finally {
            setUnpublishing(false);
        }
    };

    const isPublished = newsletter.status === 'published';

    // Load the preview HTML via a Blob URL instead of srcDoc. Iframes with
    // srcDoc don't reliably honor <meta viewport>, so @media (max-width: 480px)
    // doesn't fire even when the iframe is rendered at 375px. A Blob URL gives
    // the iframe a real document context where viewport-based responsive CSS
    // works the same as it does in real email clients.
    const previewBlobUrl = useMemo(() => {
        if (!newsletter.newsletter_html) return null;
        const blob = new Blob([newsletter.newsletter_html], { type: 'text/html;charset=UTF-8' });
        return URL.createObjectURL(blob);
    }, [newsletter.newsletter_html]);

    useEffect(() => {
        return () => {
            if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
        };
    }, [previewBlobUrl]);

    // Track unsaved edits so we can warn admins viewing the Preview tab
    // that what they see is the last saved version, not their current edits.
    const initialTop3Json = JSON.stringify(initialTop3);
    const dirty = (
        titleText !== (newsletter.title || '')
        || introText !== (newsletter.intro_text || cd.intro || '')
        || outroText !== (newsletter.outro_text || cd.outro || '')
        || issueLabel !== (cd.issue_label !== undefined ? cd.issue_label : 'Issue 01')
        || readTime !== (cd.read_time !== undefined ? cd.read_time : '')
        || JSON.stringify(stories) !== initialTop3Json
    );

    return (
        <Box m="20px" marginX={3} marginY={3}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/newsletters/admission')} sx={{ mb: 1 }}>
                Back to Admission Newsletters
            </Button>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Header title={newsletter.title} subtitle="Admission newsletter" />
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(e, v) => v && setViewMode(v)}
                    size="small"
                >
                    <ToggleButton value="preview">
                        <VisibilityIcon fontSize="small" sx={{ mr: 1 }} /> Preview
                        {dirty && <Box component="span" sx={{ ml: 1, fontSize: 11, color: 'warning.main' }}>• unsaved</Box>}
                    </ToggleButton>
                    <ToggleButton value="edit">
                        <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
                        {dirty && <Box component="span" sx={{ ml: 1, fontSize: 11, color: 'warning.main' }}>• unsaved</Box>}
                    </ToggleButton>
                </ToggleButtonGroup>

                <Box display="flex" gap={1}>
                    <Tooltip title="Download HTML">
                        <IconButton onClick={handleDownloadHtml}><DownloadIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title="Replace this newsletter's content with a fresh AI generation (irreversible)">
                        <Button
                            startIcon={<RefreshIcon />}
                            onClick={() => setRegenerateConfirmOpen(true)}
                            variant="outlined"
                            color="warning"
                            size="small"
                        >
                            Regenerate
                        </Button>
                    </Tooltip>
                    {!isPublished ? (
                        <Button startIcon={<PublishIcon />} variant="contained" color="success" onClick={handleOpenPublish}>
                            Publish
                        </Button>
                    ) : (
                        <Button onClick={() => setUnpublishDialogOpen(true)} variant="outlined" color="warning">
                            Unpublish
                        </Button>
                    )}
                    <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => setDeleteDialogOpen(true)}><DeleteIcon /></IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {viewMode === 'preview' ? (
                <>
                    {dirty && (
                        <MuiAlert severity="warning" sx={{ mb: 2 }}>
                            You have unsaved edits. The preview below shows the last <strong>saved</strong> version. Switch to Edit, click <strong>Save changes</strong>, then return here to see your edits applied.
                        </MuiAlert>
                    )}

                    {/* Preview viewport toggle */}
                    <Box display="flex" alignItems="center" justifyContent="flex-start" mb={1.5} flexWrap="wrap" gap={1}>
                        <ToggleButtonGroup
                            value={previewViewport}
                            exclusive
                            onChange={(e, v) => v && setPreviewViewport(v)}
                            size="small"
                        >
                            <ToggleButton value="desktop">Desktop</ToggleButton>
                            <ToggleButton value="mobile">Mobile (~375px)</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <MuiAlert severity="info" sx={{ mb: 2 }} icon={false}>
                        <strong>Preview is approximate.</strong> Real email clients (Gmail, Outlook, Naver, etc.) render differently and may strip or modify CSS. For ground-truth accuracy, publish to the Dev_Team group and open the result on your phone / desktop client.
                    </MuiAlert>

                    <Paper sx={{ p: previewViewport === 'mobile' ? 2 : 0, overflow: 'auto', backgroundColor: previewViewport === 'mobile' ? colors.primary[700] : '#ffffff' }}>
                        {newsletter.newsletter_html ? (
                            <Box sx={{
                                mx: 'auto',
                                width: previewViewport === 'mobile' ? 375 : '100%',
                                maxWidth: previewViewport === 'mobile' ? 375 : 'none',
                                transition: 'width 200ms ease',
                                backgroundColor: '#ffffff',
                                boxShadow: previewViewport === 'mobile' ? 3 : 0,
                                borderRadius: previewViewport === 'mobile' ? 1 : 0,
                                overflow: 'hidden',
                            }}>
                                <iframe
                                    /* key forces remount on saved-HTML change so admins
                                       always see the latest version after Save. */
                                    key={`${previewViewport}-${newsletter.edited_at || newsletter.newsletter_html?.length}`}
                                    title="Admission newsletter preview"
                                    src={previewBlobUrl}
                                    style={{
                                        width: '100%',
                                        height: '85vh',
                                        border: 0,
                                        display: 'block',
                                    }}
                                />
                            </Box>
                        ) : (
                            <Box p={4}><Typography>HTML not yet generated. Try regenerating.</Typography></Box>
                        )}
                    </Paper>
                </>
            ) : (
                <Box display="flex" flexDirection="column" gap={3}>
                    <Paper sx={{ p: 3, backgroundColor: colors.primary[600] }}>
                        <Typography variant="subtitle2" color={colors.grey[300]} mb={1}>Title</Typography>
                        <TextField
                            fullWidth
                            value={titleText}
                            onChange={(e) => setTitleText(e.target.value)}
                            placeholder="2026년 5월 Admission Newsletter"
                            inputProps={{ maxLength: 255 }}
                            helperText="Shown at the top of the newsletter list and in the admin header"
                        />
                    </Paper>

                    <Paper sx={{ p: 3, backgroundColor: colors.primary[600] }}>
                        <Typography variant="subtitle2" color={colors.grey[300]} mb={2}>Header sub-line</Typography>
                        <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                            <TextField
                                fullWidth
                                label="Issue label"
                                value={issueLabel}
                                onChange={(e) => setIssueLabel(e.target.value)}
                                onKeyDown={handleSuggestionTab(issueLabel, suggestedIssueLabel, setIssueLabel)}
                                placeholder={suggestedIssueLabel}
                                inputProps={{ maxLength: 80 }}
                                helperText={!issueLabel?.trim()
                                    ? `Press Tab to use "${suggestedIssueLabel}". Leave blank to hide.`
                                    : 'Free text. Leave blank to hide.'}
                            />
                            <TextField
                                fullWidth
                                label="Read time"
                                value={readTime}
                                onChange={(e) => setReadTime(e.target.value)}
                                onKeyDown={handleSuggestionTab(readTime, suggestedReadTime, setReadTime)}
                                placeholder={suggestedReadTime}
                                inputProps={{ maxLength: 80 }}
                                helperText={!readTime?.trim()
                                    ? `Press Tab to use "${suggestedReadTime}" (auto-computed). Leave blank to hide.`
                                    : 'Free text. Leave blank to hide.'}
                            />
                        </Box>
                    </Paper>

                    <Paper sx={{ p: 3, backgroundColor: colors.primary[600] }}>
                        <Typography variant="subtitle2" color={colors.grey[300]} mb={1}>Intro</Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            value={introText}
                            onChange={(e) => setIntroText(e.target.value)}
                            placeholder="안녕하세요. A ONE INSTITUTE 입니다. ..."
                        />
                    </Paper>

                    {stories.map((s, i) => {
                        // Lock all slots while ANY regen runs — simpler and
                        // matches the "one regen at a time" UX.
                        const anyRegenBusy = !!currentRegen || !!imageDecision;
                        const hasHeadline = !!(s.korean_headline && s.korean_headline.trim());
                        return (
                        <Paper key={i} sx={{ p: 3, backgroundColor: colors.primary[600], position: 'relative' }}>
                            {/* Header row */}
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                                <Typography variant="h6">Story {i + 1}</Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                    {s.url && (
                                        <Button
                                            size="small"
                                            startIcon={<OpenInNewIcon />}
                                            component="a"
                                            href={s.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Original article
                                        </Button>
                                    )}
                                    <Tooltip title={anyRegenBusy ? 'Another regenerate is already running — please wait.' : 'Replace this story with a fresh one from a new web search (other stories untouched)'}>
                                        <span>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="warning"
                                                startIcon={<RefreshIcon />}
                                                disabled={anyRegenBusy}
                                                onClick={() => openRegenStoryDialog(i)}
                                            >
                                                Regenerate story
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={anyRegenBusy ? 'Another regenerate is already running — please wait.' : 'Remove this story slot'}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                disabled={anyRegenBusy}
                                                onClick={() => setRemoveStoryConfirm({ open: true, index: i })}
                                                aria-label={`Remove Story ${i + 1}`}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </Box>

                            {/* Inline progress (story-level) */}
                            {regenProgress('story', i)}

                            {/* Two-column body: image | text fields */}
                            <Box
                                display="flex"
                                gap={3}
                                sx={{ flexDirection: { xs: 'column', md: 'row' } }}
                            >
                                {/* Image column */}
                                <Box
                                    sx={{
                                        flex: { xs: '1 1 auto', md: '0 0 220px' },
                                        backgroundColor: colors.primary[700],
                                        borderRadius: 2,
                                        p: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1.5,
                                    }}
                                >
                                    <Typography variant="caption" sx={{ color: colors.grey[400], textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                                        Story image
                                    </Typography>
                                    {s.image_url ? (
                                        <Box
                                            component="img"
                                            src={s.image_url}
                                            alt={`story ${i + 1}`}
                                            onClick={() => setEnlargedImageUrl(s.image_url)}
                                            sx={{
                                                width: '100%', height: 180, objectFit: 'cover',
                                                borderRadius: 2, display: 'block',
                                                cursor: 'zoom-in',
                                                transition: 'opacity 150ms ease',
                                                '&:hover': { opacity: 0.85 },
                                            }}
                                        />
                                    ) : (
                                        <Box sx={{
                                            width: '100%', height: 180, borderRadius: 2,
                                            backgroundColor: colors.primary[500],
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: colors.grey[400], fontSize: 12,
                                        }}>
                                            No image
                                        </Box>
                                    )}
                                    <Tooltip title={
                                        anyRegenBusy
                                            ? 'Another regenerate is already running — please wait.'
                                            : !hasHeadline
                                                ? 'Add a headline first — image generation needs the story content. Or click "Regenerate story" to fill the whole slot.'
                                                : 'Preview a new image for this slot. You can Keep or Discard before it replaces the current one. Story content stays untouched.'
                                    }>
                                        <span>
                                            <Button
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                startIcon={<RefreshIcon />}
                                                disabled={anyRegenBusy || !hasHeadline}
                                                onClick={() => openStyleDialog(i)}
                                            >
                                                Regenerate image
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    {regenProgress('image', i)}
                                </Box>

                                {/* Text fields column */}
                                <Box flex="1" display="flex" flexDirection="column" gap={2}>
                                    <TextField
                                        fullWidth
                                        label="Korean headline"
                                        value={s.korean_headline || ''}
                                        onChange={(e) => updateStory(i, 'korean_headline', e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        multiline minRows={2}
                                        label="Korean description"
                                        value={s.korean_description || ''}
                                        onChange={(e) => updateStory(i, 'korean_description', e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        multiline minRows={2}
                                        label="Korean summary (parent action / takeaway)"
                                        value={s.korean_summary || ''}
                                        onChange={(e) => updateStory(i, 'korean_summary', e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Source URL"
                                        value={s.url || ''}
                                        onChange={(e) => updateStory(i, 'url', e.target.value)}
                                    />
                                    <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                                        Original title: {s.original_title || '(unknown)'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                        );
                    })}

                    {/* Add-story button — visible when fewer than 3 slots exist. */}
                    {stories.length < 3 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={addStory}
                                disabled={!!currentRegen || !!imageDecision}
                            >
                                Add Story {stories.length + 1}
                            </Button>
                        </Box>
                    )}

                    <Paper sx={{ p: 3, backgroundColor: colors.primary[600] }}>
                        <Typography variant="subtitle2" color={colors.grey[300]} mb={1}>Outro</Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            value={outroText}
                            onChange={(e) => setOutroText(e.target.value)}
                            placeholder="... 감사합니다."
                        />
                    </Paper>

                    <Box display="flex" gap={2} justifyContent="flex-end">
                        <Button startIcon={<RefreshIcon />} onClick={handleRegenerateHtml} disabled={updatingHtml} variant="outlined">
                            {updatingHtml ? 'Regenerating…' : 'Regenerate HTML'}
                        </Button>
                        <Button startIcon={<SaveIcon />} onClick={handleSave} disabled={saving} variant="contained">
                            {saving ? 'Saving…' : 'Save changes'}
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Publish dialog (admin picks MailerLite groups, mirrors competition newsletter UX) */}
            <Dialog open={publishDialogOpen} onClose={() => setPublishDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Publish admission newsletter</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Mark this newsletter as published. Optionally send to subscribers via MailerLite.
                        MailerLite's campaign dashboard is the source of truth for what was delivered.
                    </DialogContentText>

                    <FormGroup>
                        <FormControlLabel
                            control={<Checkbox checked={sendViaMailerLite} onChange={(e) => setSendViaMailerLite(e.target.checked)} />}
                            label="Send to subscribers via MailerLite"
                        />
                    </FormGroup>

                    {sendViaMailerLite && (
                        <Box mt={2}>
                            <Typography variant="subtitle2" mb={1}>Select recipient group(s):</Typography>
                            {loadingGroups && <CircularProgress size={20} />}
                            {groupsError && <MuiAlert severity="error">{groupsError}</MuiAlert>}
                            <FormGroup>
                                {mailerliteGroups.map((g) => (
                                    <FormControlLabel
                                        key={g.id}
                                        control={<Checkbox
                                            checked={selectedGroupIds.includes(g.id)}
                                            onChange={(e) => setSelectedGroupIds((prev) =>
                                                e.target.checked ? [...prev, g.id] : prev.filter((id) => id !== g.id)
                                            )}
                                        />}
                                        label={`${g.name} (${g.active_count ?? 0} active)`}
                                    />
                                ))}
                            </FormGroup>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handlePublish} disabled={publishing} variant="contained" color="success">
                        {publishing ? 'Publishing…' : 'Publish'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={unpublishDialogOpen} onClose={() => setUnpublishDialogOpen(false)}>
                <DialogTitle>Unpublish newsletter</DialogTitle>
                <DialogContent>
                    <DialogContentText>Revert the newsletter to draft status. Already-sent emails to subscribers cannot be recalled.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUnpublishDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleUnpublish} disabled={unpublishing} variant="outlined" color="warning">
                        {unpublishing ? 'Unpublishing…' : 'Unpublish'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={regenerateConfirmOpen}
                onClose={() => !regenerating && setRegenerateConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Replace this newsletter?</DialogTitle>
                <DialogContent>
                    <MuiAlert severity="warning" sx={{ mb: 2 }}>
                        Regenerating will <strong>replace this newsletter's current content</strong> — all stories, images, intro, outro, and any edits you've made will be overwritten. This cannot be undone.
                    </MuiAlert>
                    <DialogContentText>
                        Want to keep this draft and create a separate new one instead?
                        Cancel here, then go back to the Admission tab and click <strong>Generate Admission Newsletter</strong>. The system will auto-suffix the title (e.g. "{newsletter.title} 2") so both drafts coexist.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRegenerateConfirmOpen(false)} disabled={regenerating}>
                        Cancel
                    </Button>
                    <Button onClick={handleRetry} disabled={regenerating} variant="contained" color="warning">
                        {regenerating ? 'Starting…' : 'Replace this draft'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={regenStoryConfirm.open}
                onClose={() => setRegenStoryConfirm({ open: false, index: -1 })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Regenerate Story {regenStoryConfirm.index + 1}?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        AI will search tier-1 admissions sources for a fresh in-window story and generate a new image. Takes ~40 seconds. Stories listed below are sent to the AI as a hard "do not return these" instruction.
                    </DialogContentText>

                    {/* Group 1 — current top3 (auto-excluded, read-only) */}
                    <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Currently in this newsletter ({stories.filter((s) => s?.url).length})
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: colors.grey[400], mb: 1 }}>
                        Auto-excluded — the AI cannot return any of these.
                    </Typography>
                    <Box
                        sx={{
                            border: `1px solid ${colors.grey[700]}`,
                            borderRadius: 1,
                            mb: 2.5,
                            backgroundColor: colors.primary[800],
                        }}
                    >
                        {stories.map((s, sIdx) => {
                            if (!s?.url) return null;
                            const isReplacing = sIdx === regenStoryConfirm.index;
                            return (
                                <Box
                                    key={`current-${sIdx}`}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1,
                                        px: 1.5,
                                        py: 1,
                                        borderBottom: sIdx === stories.length - 1 ? 'none' : `1px solid ${colors.grey[700]}`,
                                        opacity: isReplacing ? 1 : 0.75,
                                    }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.25 }}>
                                            Story {sIdx + 1}
                                            {isReplacing && (
                                                <Box component="span" sx={{ ml: 1, fontSize: '11px', color: 'warning.main', fontWeight: 600 }}>
                                                    REPLACING
                                                </Box>
                                            )}
                                            {s.original_title && (
                                                <Box component="span" sx={{ ml: 1, fontWeight: 400, color: colors.grey[300] }}>
                                                    — {s.original_title}
                                                </Box>
                                            )}
                                        </Typography>
                                        <a
                                            href={s.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                fontSize: '12px',
                                                color: '#90caf9',
                                                textDecoration: 'underline',
                                                wordBreak: 'break-all',
                                            }}
                                        >
                                            {s.url}
                                        </a>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Group 2 — admin-curated rejection history (editable) */}
                    <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Other excluded stories ({dialogExcludeList.length})
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: colors.grey[400], mb: 1 }}>
                        Previously rejected or manually added. Stays across regens until you remove it.
                    </Typography>

                    {dialogExcludeList.length === 0 ? (
                        <Typography variant="body2" sx={{ color: colors.grey[400], mb: 2, fontStyle: 'italic' }}>
                            (No stories rejected yet.)
                        </Typography>
                    ) : (
                        <Box
                            sx={{
                                maxHeight: 200,
                                overflowY: 'auto',
                                border: `1px solid ${colors.grey[700]}`,
                                borderRadius: 1,
                                mb: 2,
                            }}
                        >
                            {dialogExcludeList.map((entry, idx) => (
                                <Box
                                    key={`${entry.url}-${idx}`}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1,
                                        px: 1.5,
                                        py: 1,
                                        borderBottom: idx === dialogExcludeList.length - 1 ? 'none' : `1px solid ${colors.grey[700]}`,
                                    }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        {entry.title && (
                                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.25 }}>
                                                {entry.title}
                                            </Typography>
                                        )}
                                        <a
                                            href={entry.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                fontSize: '12px',
                                                color: '#90caf9',
                                                textDecoration: 'underline',
                                                wordBreak: 'break-all',
                                            }}
                                        >
                                            {entry.url}
                                        </a>
                                    </Box>
                                    <Tooltip title="Remove from exclude list">
                                        <IconButton
                                            size="small"
                                            onClick={() => removeFromExcludeList(entry.url)}
                                            sx={{ flexShrink: 0 }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Add a story to exclude
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <TextField
                                size="small"
                                fullWidth
                                label="URL"
                                placeholder="https://…"
                                value={dialogAddUrl}
                                onChange={(e) => { setDialogAddUrl(e.target.value); setDialogAddError(''); }}
                                error={!!dialogAddError}
                                helperText={dialogAddError || ' '}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToExcludeList(); } }}
                            />
                            <TextField
                                size="small"
                                fullWidth
                                label="Title (optional)"
                                value={dialogAddTitle}
                                onChange={(e) => setDialogAddTitle(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToExcludeList(); } }}
                            />
                        </Box>
                        <Button
                            variant="outlined"
                            onClick={addToExcludeList}
                            disabled={!dialogAddUrl.trim()}
                            sx={{ mt: 0.5 }}
                        >
                            Add
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRegenStoryConfirm({ open: false, index: -1 })}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleRegenerateStory(regenStoryConfirm.index)}
                        variant="contained"
                        color="warning"
                        disabled={!!currentRegen || !!imageDecision}
                    >
                        {currentRegen ? 'A regen is running…' : 'Regenerate'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete admission newsletter</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure? This permanently deletes the newsletter row and its content.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!swapDecision}
                onClose={() => !reverting && setSwapDecision(null)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    Story {swapDecision ? swapDecision.index + 1 : ''} regenerated — keep new or revert?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        The new story is already saved. If you'd rather keep the previous version, click "Revert" — the AI will treat the regenerated story as rejected so it won't be picked again.
                    </DialogContentText>

                    {swapDecision && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: 2,
                        }}>
                            {/* PREVIOUS */}
                            <Box sx={{
                                flex: 1,
                                p: 2,
                                border: `1px solid ${colors.grey[700]}`,
                                borderRadius: 1,
                                backgroundColor: colors.primary[700],
                            }}>
                                <Typography variant="caption" sx={{ color: colors.grey[400], textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                                    Previous version
                                </Typography>
                                {swapDecision.oldStory.image_url ? (
                                    <Box
                                        component="img"
                                        src={swapDecision.oldStory.image_url}
                                        alt="previous"
                                        sx={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 1, my: 1.5, display: 'block' }}
                                    />
                                ) : (
                                    <Box sx={{
                                        width: '100%', height: 160, borderRadius: 1, my: 1.5,
                                        backgroundColor: colors.primary[500],
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: colors.grey[400], fontSize: 12,
                                    }}>(no image)</Box>
                                )}
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {swapDecision.oldStory.korean_headline || '(no headline)'}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: colors.grey[300], mb: 1 }}>
                                    {swapDecision.oldStory.original_title || ''}
                                </Typography>
                                <Typography variant="body2" sx={{ color: colors.grey[200], fontSize: 13 }}>
                                    {swapDecision.oldStory.korean_description || ''}
                                </Typography>
                                {swapDecision.oldStory.url && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <a
                                            href={swapDecision.oldStory.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#90caf9', fontSize: 12, textDecoration: 'underline', wordBreak: 'break-all' }}
                                        >
                                            {swapDecision.oldStory.url}
                                        </a>
                                    </Box>
                                )}
                            </Box>

                            {/* NEW */}
                            <Box sx={{
                                flex: 1,
                                p: 2,
                                border: '2px solid',
                                borderColor: 'success.main',
                                borderRadius: 1,
                                backgroundColor: colors.primary[700],
                            }}>
                                <Typography variant="caption" sx={{ color: 'success.main', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                                    New version (saved)
                                </Typography>
                                {swapDecision.newStory.image_url ? (
                                    <Box
                                        component="img"
                                        src={swapDecision.newStory.image_url}
                                        alt="new"
                                        sx={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 1, my: 1.5, display: 'block' }}
                                    />
                                ) : (
                                    <Box sx={{
                                        width: '100%', height: 160, borderRadius: 1, my: 1.5,
                                        backgroundColor: colors.primary[500],
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: colors.grey[400], fontSize: 12,
                                    }}>(no image)</Box>
                                )}
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {swapDecision.newStory.korean_headline || '(no headline)'}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: colors.grey[300], mb: 1 }}>
                                    {swapDecision.newStory.original_title || ''}
                                </Typography>
                                <Typography variant="body2" sx={{ color: colors.grey[200], fontSize: 13 }}>
                                    {swapDecision.newStory.korean_description || ''}
                                </Typography>
                                {swapDecision.newStory.url && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <a
                                            href={swapDecision.newStory.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#90caf9', fontSize: 12, textDecoration: 'underline', wordBreak: 'break-all' }}
                                        >
                                            {swapDecision.newStory.url}
                                        </a>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={revertSwapDecision}
                        disabled={reverting}
                        color="warning"
                    >
                        {reverting ? 'Reverting…' : 'Revert to previous'}
                    </Button>
                    <Button
                        onClick={() => setSwapDecision(null)}
                        disabled={reverting}
                        variant="contained"
                        color="success"
                    >
                        Keep new
                    </Button>
                </DialogActions>
            </Dialog>

            {/*
              * Per-regen style tweak dialog. Opens FIRST when admin clicks
              * "Regenerate image" — prefilled with default cues, admin can
              * tweak then click Generate to run the preview regen. Style cues
              * are NOT persisted; they only affect this single regen.
              */}
            <Dialog
                open={!!styleDialog}
                onClose={() => setStyleDialog(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Image style — story {styleDialog ? styleDialog.index + 1 : ''}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Tweak the style cues below to nudge this single regenerated image. The image model works best with English. These changes don't persist — they only affect this one regen.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Style cues"
                        value={styleDialog?.cues || ''}
                        onChange={(e) => setStyleDialog((sd) => sd ? { ...sd, cues: e.target.value } : sd)}
                        inputProps={{ maxLength: 500 }}
                        helperText={`${(styleDialog?.cues || '').length}/500 characters`}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setStyleDialog((sd) => sd ? { ...sd, cues: getDefaultStyleCues(newsletter.month) } : sd)}
                        color="inherit"
                    >
                        Reset to default
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={() => setStyleDialog(null)}>Cancel</Button>
                    <Button
                        onClick={() => {
                            if (!styleDialog) return;
                            const { index, cues } = styleDialog;
                            const trimmed = (cues || '').trim();
                            const isDefault = trimmed === getDefaultStyleCues(newsletter.month);
                            setStyleDialog(null);
                            handleRegenerateImage(index, isDefault ? null : trimmed);
                        }}
                        variant="contained"
                        color="warning"
                        disabled={!(styleDialog?.cues || '').trim()}
                    >
                        Generate
                    </Button>
                </DialogActions>
            </Dialog>

            {/*
              * Image-only preview decision modal. Opens after a preview regen
              * succeeds — the new image lives in S3 but isn't on the newsletter
              * yet. Keep persists it; Discard closes without changing anything.
              */}
            <Dialog
                open={!!imageDecision}
                onClose={() => !committingImage && setImageDecision(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Story {imageDecision ? imageDecision.index + 1 : ''} image — keep new or discard?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        The new image is a preview — it hasn't replaced the current image yet. Click "Keep new" to save it, or "Discard" to throw it away and keep the current image.
                    </DialogContentText>

                    {imageDecision && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: 2,
                        }}>
                            {/* CURRENT */}
                            <Box sx={{
                                flex: 1,
                                p: 2,
                                border: `1px solid ${colors.grey[700]}`,
                                borderRadius: 1,
                                backgroundColor: colors.primary[700],
                            }}>
                                <Typography variant="caption" sx={{ color: colors.grey[400], textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                                    Current image
                                </Typography>
                                {imageDecision.oldImageUrl ? (
                                    <Box
                                        component="img"
                                        src={imageDecision.oldImageUrl}
                                        alt="current"
                                        sx={{ width: '100%', height: 280, objectFit: 'cover', borderRadius: 1, mt: 1.5, display: 'block' }}
                                    />
                                ) : (
                                    <Box sx={{
                                        width: '100%', height: 280, borderRadius: 1, mt: 1.5,
                                        backgroundColor: colors.primary[500],
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: colors.grey[400], fontSize: 12,
                                    }}>(no image)</Box>
                                )}
                            </Box>

                            {/* NEW */}
                            <Box sx={{
                                flex: 1,
                                p: 2,
                                border: '2px solid',
                                borderColor: 'success.main',
                                borderRadius: 1,
                                backgroundColor: colors.primary[700],
                            }}>
                                <Typography variant="caption" sx={{ color: 'success.main', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                                    New (preview)
                                </Typography>
                                {imageDecision.newImageUrl ? (
                                    <Box
                                        component="img"
                                        src={imageDecision.newImageUrl}
                                        alt="new preview"
                                        sx={{ width: '100%', height: 280, objectFit: 'cover', borderRadius: 1, mt: 1.5, display: 'block' }}
                                    />
                                ) : (
                                    <Box sx={{
                                        width: '100%', height: 280, borderRadius: 1, mt: 1.5,
                                        backgroundColor: colors.primary[500],
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: colors.grey[400], fontSize: 12,
                                    }}>(generation failed)</Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={discardNewImage}
                        disabled={committingImage}
                        color="warning"
                    >
                        Discard
                    </Button>
                    <Button
                        onClick={keepNewImage}
                        disabled={committingImage}
                        variant="contained"
                        color="success"
                    >
                        {committingImage ? 'Saving…' : 'Keep new'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={removeStoryConfirm.open}
                onClose={() => setRemoveStoryConfirm({ open: false, index: -1 })}
            >
                <DialogTitle>Remove Story {removeStoryConfirm.index + 1}?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This removes the slot from this draft — headline, description, summary, and image will be discarded. You can add a new slot back later. Save the newsletter to persist this change.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoveStoryConfirm({ open: false, index: -1 })}>
                        Cancel
                    </Button>
                    <Button onClick={executeRemoveStory} variant="contained" color="error">
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Click-to-enlarge image dialog. Opens when the admin clicks any
              * story-slot thumbnail. Click backdrop or press Esc to close. */}
            <Dialog
                open={!!enlargedImageUrl}
                onClose={() => setEnlargedImageUrl(null)}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { backgroundColor: 'transparent', boxShadow: 'none' } }}
            >
                <Box
                    onClick={() => setEnlargedImageUrl(null)}
                    sx={{ cursor: 'zoom-out', display: 'flex', justifyContent: 'center' }}
                >
                    {enlargedImageUrl && (
                        <Box
                            component="img"
                            src={enlargedImageUrl}
                            alt="enlarged"
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '90vh',
                                objectFit: 'contain',
                                borderRadius: 1,
                                display: 'block',
                            }}
                        />
                    )}
                </Box>
            </Dialog>

            <Alert
                successAlertOpen={successAlertOpen}
                setSuccessAlertOpen={setSuccessAlertOpen}
                errorAlertOpen={errorAlertOpen}
                setErrorAlertOpen={setErrorAlertOpen}
                successMsg={successAlertMessage}
                errorMsg={errorAlertMessage}
                vertical='bottom'
                horizontal='left'
            />
        </Box>
    );
};

export default AdmissionNewsletterEditor;
